import * as toolbox from '../components/common/toolbox';
import * as types from './action-types';
import firebase from 'firebase/compat/app';
import { getDoc } from 'firebase/firestore';
import { DEFAULT_SCHEDULE } from 'components/common/constants';
import _ from 'lodash';

const firestore = firebase.firestore();

/*-----------------------------------------------*/
/*  IMPORT EXPORT
/*-----------------------------------------------*/

export async function exportProfiles(handle, columns, type) {

	var profiles = [];
	var snapshot = await firestore.collection(handle + '/profiles/profiles').where('type', '==', type).get();
	snapshot.forEach((doc) => {
		var entry = {};
		columns.forEach((column) => {
			entry[column] = doc.data()[column];
		});
		profiles.push({ 
			...entry, 
			id: doc.id, 
			addresses: JSON.stringify(doc.data().addresses)?.replace(',', '|'), 
			contacts: JSON.stringify(doc.data().contacts)?.replace(',', '|'), 
		});
	});

	return profiles;
}

export async function importProfile(handle, profileForm, profile_type) {

	var profileId;
	var addressId;
	var contactId;
	var batch = firestore.batch();
	var profile = {};

	profileId = await nextProfileId(handle, batch);

	var { addressName, addressLine1, addressLine2, city, state, zip, email, firstName, lastName, mobile, phone, fax, contactTypeId,
		addressTypeId, notes, ...new_profile } = profileForm;

	/* Contact ---------------------------------------------------*/

	if (firstName || lastName || email || mobile || phone) {
		contactId = await nextContactId(handle, batch);
		var contact = { contactTypeId: (contactTypeId) ? contactTypeId : '2', firstName, lastName, email, mobile, phone, fax, notes };
		contact.profileIds = [profileId];
		contact.notifyViaEmail = true;
		contact.notifyViaSms = true;
		contact.deleted = false;
		contact.created = new Date();
		contact.modified = new Date();
		if (profileForm?.importId) contact.importId = profileForm.importId;

		const contactRef = firestore.collection(handle + '/contacts/contacts');
		batch.set(contactRef.doc(contactId), { ...contact }, { merge: true });
	}

	/* Address ---------------------------------------------------*/

	if (addressName || addressLine1 || addressLine2 || city || state || zip) {
		addressId = await nextAddressId(handle, batch);
		var address = { addressId, addressTypeId: (addressTypeId) ? addressTypeId : '5', contactIds: [contactId], addressName, 
			addressLine1, addressLine2, city, state, zip, };
		address.profileId = profileId;
		address.deleted = false;
		address.created = new Date();
		address.modified = new Date();

		Object.keys(address).forEach((key) => { if (address[key] == undefined) delete address[key]; });

		const addressRef = firestore.collection(handle + '/addresses/addresses');
		batch.set(addressRef.doc(addressId), { ...address }, { merge: true });
	}

	/* profile ---------------------------------------------------*/

	profile = { ...new_profile, phone: (phone) ? phone : mobile };
	profile.created = new Date();
	profile.deleted = false;

	profile.modified = new Date();
	profile.type = profile_type;

	const profileRef = firestore.collection(handle + '/profiles/profiles');
	batch.set(profileRef.doc(profileId), { ...profile }, { merge: true });

	await batch.commit().catch((error) => {
		toolbox.process_error(error, 'Profile NOT Saved!');
	});
};

/*-----------------------------------------------*/
/*  EMPLOYEES
/*-----------------------------------------------*/

export function subEmployees(handle) {

	return async dispatch => {

		var unsubscribe = firestore.collection(handle + '/profiles/profiles/').where('type', '==', 'employee').onSnapshot(async (snapshot) => {

			dispatch({ type: types.EMPLOYEES + '_PENDING' });

			var employees = [];
			snapshot.forEach((doc) => {
				employees.push({ ...doc.data(), id: doc.id });
			});

			dispatch({ type: types.EMPLOYEES + '_FULFILLED', data: employees, unsubscribe });
		});
	};
}

/*-----------------------------------------------*/
/*  PROFILES
/*-----------------------------------------------*/

export function subProfile(handle, id, profile_type) {

	return dispatch => {

		dispatch({ type: types[profile_type] + '_PENDING' });

		var unsubscribe = firestore.collection(handle + '/profiles/profiles').doc(id).onSnapshot(async (doc) => {

			if (doc.exists) {
				var profile = { ...doc.data(), id: doc.id };
				var addresses = [];
				var contacts = [];
				var user = null;

				const snapshot1 = await firestore.collection(handle + '/addresses/addresses').where('profileId', '==', profile.id).where('deleted', '==', false).get();
				snapshot1.forEach((doc) => {
					addresses.push({ ...doc.data(), id: doc.id });
				});

				const snapshot2 = await firestore.collection(handle + '/contacts/contacts').where('profileIds', 'array-contains', profile.id).where('deleted', '==', false).get();
				snapshot2.forEach((doc) => {
					contacts.push({ ...doc.data(), id: doc.id });
				});

				if (contacts?.[0]?.email) {
					const userDoc = await firestore.collection(handle + '/users/users').doc(contacts[0].email).get();
					user = userDoc.data();
				}

				profile = { ...profile, ...user };
				profile.addresses = addresses;
				
				if (profile_type == 'EMPLOYEE') {
					profile.contact = contacts[0];
				} else {
					profile.contacts = contacts;
				}
			}

			dispatch({ type: types[profile_type] + '_FULFILLED', data: profile, unsubscribe });
		});
	};
}

export function subVendors(handle) {

	// displayName only for the purpose of form dropdown

	return async dispatch => {

		var unsubscribe = firestore.collection(handle + '/profiles/profiles/').where('type', '==', 'vendor').onSnapshot(async (snapshot) => {

			dispatch({ type: types.VENDORS + '_PENDING' });

			var vendors = [];
			snapshot.forEach((doc) => {
				vendors.push({ displayName: doc.data().displayName, id: doc.id });
			});
			vendors = _.orderBy(vendors, ['displayName'], ['asc']);

			dispatch({ type: types.VENDORS + '_FULFILLED', data: vendors, unsubscribe });
		});
	};
}

export function saveProfile({ handle, profileId, profileForm, profileType, prevEmail, silent = false, callback }) {

	// Primarily Updates Profile, but also creates associated Contact, Address, & User for new profiles.

	return async dispatch => {

		dispatch({ type: types['PROFILE'] + '_SAVE_PENDING' });

		var addressId;
		var contactId;
		var user;
		var batch = firestore.batch();
		var type = profileType?.toLowerCase();

		var { addressName, addressLine1, addressLine2, city, state, zip, profileIds, email, firstName, lastName, mobile, phone, contactTypeId, 
			addressTypeId, hireDate, terminationDate, notifyViaEmail, notifyViaSms, photoUrl, roles,
			activated, ...profile_fields } = profileForm;

		const fields = [
			'departmentId', 
			'tags', 
			'notes',
			'roles',
			'certifications', 
			'schedule',
			'deleted',
			'type',
		];

		var profile = toolbox.sanitize(profile_fields, fields);
	
		if (roles) profile = { ...profile_fields, roles: roles.map((role) => role.value) };

		if (!parseInt(profileId)) {
			// Check if an employee is already registered under this email
			if (profileType == "EMPLOYEE" && email) {
				const userRef = firestore.collection(handle + "/users/users").doc(profileForm.email);
				const userDoc = await getDoc(userRef);
				if (userDoc.exists() && !userDoc.data()?.['deleted']) {
					toolbox.process_error('A user already exists with this email!');
					dispatch({ type: types[profileType] + '_SAVE_FULFILLED' });
					return;
				}
			}
			
			profile.type = type;
			profile.schedule = DEFAULT_SCHEDULE;
			profileId = await nextProfileId(handle, batch);

			/* Contact ---------------------------------------------------*/

			if (firstName || lastName || email || mobile || phone) {
				contactId = await nextContactId(handle, batch);
				var contact = { contactTypeId: (contactTypeId) ? contactTypeId : null, firstName, lastName, email, 
					mobile, phone, hireDate, terminationDate };
				contact.profileIds = [profileId];
				contact.notifyViaEmail = true;
				contact.notifyViaSms = true;
				contact.photoUrl = photoUrl;
				contact.type = type;
				contact.deleted = false;
				contact.created = new Date();
				contact.modified = new Date();
				if (profileForm?.importId) contact.importId = profileForm.importId;

				const contactRef = firestore.collection(handle + '/contacts/contacts');
				batch.set(contactRef.doc(contactId), { ...contact }, { merge: true });
			}

			/* Address ---------------------------------------------------*/

			if (addressName || addressLine1 || addressLine2 || city || state || zip) {
				addressId = await nextAddressId(handle, batch);
				var address = { addressId, addressTypeId, addressName, addressLine1, addressLine2, city, state, zip, };
				if (contactId) address.contactIds = [contactId];
				address.profileId = profileId;
				address.deleted = false;
				address.created = new Date();
				address.modified = new Date();

				const addressRef = firestore.collection(handle + '/addresses/addresses');
				batch.set(addressRef.doc(addressId), { ...address }, { merge: true });
			}

			/* user ------------------------------------------------------*/

			if (profileType == "EMPLOYEE" && email) {
				user = { roles: roles.map((role) => role.value) };
				user.deleted = false;
				user.firstName = firstName;
				user.lastName = lastName;
				user.profileId = profileId;

				const usersRef = firestore.collection(handle + '/users/users');
				batch.set(usersRef.doc(email), user, { merge: true });
			}

			/* profile ---------------------------------------------------*/

			profile.created = new Date();
			profile.deleted = false;

		} else {
			if (profileType == "EMPLOYEE") {
				const usersRef = firestore.collection(handle + '/users/users');
				if (prevEmail && prevEmail != email) {
					batch.delete(usersRef.doc(prevEmail));
				}
				if (email) {
					user = {};
					if (roles) user.roles = roles.map((role) => role.value);
					user.deleted = false;
					user.firstName = firstName;
					user.lastName = lastName;
					user.profileId = profileId;

					batch.set(usersRef.doc(email), user, { merge: true });
				}
			}
		}

		delete profile.id;
		profile.modified = new Date();

		const profileRef = firestore.collection(handle + '/profiles/profiles');
		batch.set(profileRef.doc(profileId), { ...profile }, { merge: true });

		await batch.commit().then(() => {
			dispatch({ type: types['PROFILE'] + '_SAVE_FULFILLED', data: profile });
			(!silent) && window.toastr.success('The Profile has been successfully saved/updated', 'Profile Saved!');
			if (typeof callback === 'function') callback(profileId, addressId, contactId);
		}).catch((error) => {
			toolbox.process_error(error, 'Profile NOT Saved!');
		});
	};
}

export function deleteProfile(handle, profile, profile_type, editedBy, callback) {

	return async dispatch => {

		dispatch({ type: types[profile_type] + '_SAVE_PENDING' });

		var batch = firestore.batch();

		const profileRef = firestore.collection(handle + '/profiles/profiles');
		batch.set(profileRef.doc(profile.id), { deleted: true, modified: new Date() }, { merge: true });

		batch.commit().then(() => {
			dispatch({ type: types[profile_type] + '_SAVE_FULFILLED' });
			window.toastr.success('The Profile record has been Archived', 'Profile Archived!');
			if (typeof callback === 'function') callback();
		}).catch((error) => {
			toolbox.process_error(error, 'Profile NOT Archived!');
		});
	};
}

/*-----------------------------------------------*/
/*  ADDRESSES
/*-----------------------------------------------*/

export function getAddress(handle, id) {

	return dispatch => {

		dispatch({ type: types.ADDRESS + '_PENDING' });

		firestore.collection(handle + '/addresses/addresses').doc(id).get().then((doc) => {
			var address = { ...doc.data(), id: doc.id };

			dispatch({ type: types.ADDRESS + '_FULFILLED', data: address });
		});
	};
}
export function clearAddress() {

	return dispatch => {
		dispatch({ type: types.ADDRESS + '_CLEAR' });
	};
}
export function saveAddress(handle, profileId, address, callback) {

	return async dispatch => {

		dispatch({ type: types.ADDRESS + '_SAVE_PENDING' });

		const fields = [
			'addressTypeId', 
			'addressName',
			'addressLine1', 
			'addressLine2', 
			'city', 
			'state', 
			'zip',
			'deleted',
		];

		var batch = firestore.batch();
		var addressId = address.addressId;
		var update = toolbox.sanitize(address, fields);

		if (!addressId) {
			addressId = await nextAddressId(handle, batch);
			update.profileId = profileId;
			update.created = new Date();
			update.deleted = false;
		}
		update.modified = new Date();

		var { addressTypeId, addressName, addressLine1, addressLine2, city, state, zip } = address;
		update = { ...update, addressTypeId: (addressTypeId) ? addressTypeId : null, addressName, addressLine1, addressLine2, 
			city, state, zip };

		batch.set(firestore.collection(handle + '/addresses/addresses').doc(addressId), { ...update }, { merge: true });

		batch.commit().then((doc) => {
			dispatch({ type: types.ADDRESS + '_FULFILLED', data: address });
			window.toastr.success('The Address record has been successfully saved/updated', 'Address Saved!');
			if (typeof callback === 'function') callback(addressId);
		});
	};
}

/*-----------------------------------------------*/
/*  CONTACTS
/*-----------------------------------------------*/

export function saveContact(handle, profileId, contact, type, callback) {

	return async dispatch => {

		dispatch({ type: types.CONTACT + '_SAVE_PENDING' });

		const fields = [
			'contactTypeId', 
			'profileIds',
			'firstName', 
			'lastName', 
			'email', 
			'mobile', 
			'phone',
			'notes',
			'hireDate', 
			'terminationDate',
			'photoUrl',
			'type',
			'deleted',
		];
		
		var batch = firestore.batch();
		var contactId = contact.id;
		var update = toolbox.sanitize(contact, fields);

		if (!parseInt(contactId)) {
			contactId = await nextContactId(handle, batch);
			update.profileIds = firebase.firestore.FieldValue.arrayUnion(profileId);
			update.type = type;
			update.created = new Date();
			update.deleted = false;
		} else {
			contactId = contact.id;
		}
		update.modified = new Date();

		if (parseInt(contact.prevLinkAddressId)) {
			batch.update(firestore.collection(handle + '/addresses/addresses').doc(contact.prevLinkAddressId), {
				contactIds: firebase.firestore.FieldValue.arrayRemove(contactId)
			});
		}
		if (parseInt(contact.linkAddressId)) {
			batch.update(firestore.collection(handle + '/addresses/addresses').doc(contact.linkAddressId), {
				contactIds: firebase.firestore.FieldValue.arrayUnion(contactId)
			});
		}

		batch.set(firestore.collection(handle + '/contacts/contacts').doc(contactId), { ...update }, { merge: true });
		batch.set(firestore.collection(handle + '/profiles/profiles').doc(profileId), { modified: new Date() }, { merge: true });

		batch.commit().then((doc) => {
			dispatch({ type: types.CONTACT + '_FULFILLED', data: contact });
			if (typeof callback === 'function') callback(contactId);
		});
	};
}

/*-----------------------------------------------*/
/*  CUSTOMERS
/*-----------------------------------------------*/

// export function getCustomerFromSearch(handle, id) {

// 	return dispatch => {

// 		dispatch({ type: types.CUSTOMER_SEARCH + '_PENDING' });

// 		firestore.collection(handle + '/profiles/profiles').doc(id).get().then(async (doc) => {

// 			if (doc.exists) {
// 				var customer = { ...doc.data(), id: doc.id };
// 				var addresses = [];
// 				var contacts = [];

// 				var snapshot1 = await firestore.collection(handle + '/addresses/addresses').where('profileId', '==', customer.id).where('deleted', '==', false).get();
// 				snapshot1.forEach((doc) => {
// 					addresses.push({ ...doc.data(), addressId: doc.id });
// 				});

// 				var snapshot2 = await firestore.collection(handle + '/contacts/contacts').where('profileIds', 'array-contains', customer.id).where('deleted', '==', false).get();
// 				snapshot2.forEach((doc) => {
// 					contacts.push({ ...doc.data(), contactId: doc.id });
// 				});

// 				customer.addresses = addresses;
// 				customer.contacts = contacts;
// 			}

// 			dispatch({ type: types.CUSTOMER_SEARCH + '_FULFILLED', data: customer });
// 		});
// 	};
// }
// export function clearCustomerFromSearch(handle, id) {

// 	return dispatch => {

// 		dispatch({ type: types.CUSTOMER_SEARCH + '_CLEAR' });
// 	};
// }

export function clearContact() {

	return dispatch => {
		dispatch({ type: types.CONTACT + '_CLEAR' });
	};
}
export function getContact(handle, id) {

	return dispatch => {

		dispatch({ type: types.CONTACT + '_PENDING' });

		firestore.collection(handle + '/contacts/contacts').doc(id).get().then((doc) => {
			var contact = { ...doc.data(), id: doc.id };

			dispatch({ type: types.CONTACT + '_FULFILLED', data: contact });
		});
	};
}
export function subContact(handle, id) {

	return dispatch => {

		dispatch({ type: types.CONTACT + '_PENDING' });

		var unsubscribe = firestore.collection(handle + '/contacts/contacts').doc(id).onSnapshot(async (doc) => {

			var contact = { ...doc.data(), id: doc.id };

			dispatch({ type: types.CONTACT + '_FULFILLED', data: contact, unsubscribe });
		});
	};
}

/*-----------------------------------------------*/
/*  NEXT IDS
/*-----------------------------------------------*/

async function nextProfileId(handle, batch) {
	const table = 'profiles';
	const field = 'nextProfileId';
	const startingId = 1000;

	return toolbox.nextId(handle, batch, table, field, startingId);
}
async function nextAddressId(handle, batch) {
	const table = 'addresses';
	const field = 'nextAddressId';
	const startingId = 1000;

	return toolbox.nextId(handle, batch, table, field, startingId);
}
async function nextContactId(handle, batch) {
	const table = 'contacts';
	const field = 'nextContactId';
	const startingId = 1000;

	return toolbox.nextId(handle, batch, table, field, startingId);
}
